import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      await loginWithGoogle(credentialResponse.credential);
      toast.success('Welcome! Logged in successfully 🎉');
      navigate('/dashboard');
    } catch (err) {
      toast.error('Login failed. Please try again.');
      console.error(err);
    }
  };

  return (
    <div style={styles.page}>
      {/* Animated background blobs */}
      <div style={styles.blob1} />
      <div style={styles.blob2} />
      <div style={styles.blob3} />

      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrap}>
          <span style={styles.logoIcon}>🔔</span>
        </div>

        <h1 style={styles.title}>SubRemind</h1>
        <p style={styles.subtitle}>
          Never let a subscription catch you off guard again
        </p>

        <div style={styles.featureList}>
          {[
            { icon: '📋', text: 'Track all subscriptions in one place' },
            { icon: '⏰', text: 'Get email & WhatsApp reminders' },
            { icon: '🔄', text: 'Auto-calculate renewal dates' },
          ].map((f, i) => (
            <div key={i} style={styles.feature}>
              <span style={styles.featureIcon}>{f.icon}</span>
              <span style={styles.featureText}>{f.text}</span>
            </div>
          ))}
        </div>

        <div style={styles.divider}>
          <span style={styles.dividerText}>Sign in to get started</span>
        </div>

        <div style={styles.googleWrap}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => toast.error('Google login failed')}
            useOneTap
            theme="filled_blue"
            size="large"
            shape="pill"
            text="continue_with"
          />
        </div>

        <p style={styles.legal}>
          By signing in, you agree to our Terms of Service and Privacy Policy.
          Your data is stored securely and never shared.
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
  },
  blob1: {
    position: 'absolute', top: '-100px', right: '-80px',
    width: '400px', height: '400px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%', filter: 'blur(60px)',
  },
  blob2: {
    position: 'absolute', bottom: '-120px', left: '-80px',
    width: '450px', height: '450px',
    background: 'rgba(255,200,100,0.15)',
    borderRadius: '50%', filter: 'blur(80px)',
  },
  blob3: {
    position: 'absolute', top: '40%', left: '20%',
    width: '200px', height: '200px',
    background: 'rgba(255,100,150,0.1)',
    borderRadius: '50%', filter: 'blur(50px)',
  },
  card: {
    background: 'rgba(255,255,255,0.97)',
    backdropFilter: 'blur(20px)',
    borderRadius: '28px',
    padding: '48px 40px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 32px 64px rgba(0,0,0,0.25)',
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
  },
  logoWrap: {
    width: '80px', height: '80px',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    borderRadius: '24px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 8px 24px rgba(102,126,234,0.4)',
  },
  logoIcon: { fontSize: '36px' },
  title: {
    fontSize: '36px', fontWeight: '800',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    margin: '0 0 8px',
    fontFamily: '"Georgia", serif',
  },
  subtitle: {
    color: '#666', fontSize: '15px', margin: '0 0 28px', lineHeight: '1.5',
  },
  featureList: {
    background: 'linear-gradient(135deg, #f8f0ff, #eff6ff)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '28px',
    textAlign: 'left',
  },
  feature: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '6px 0',
  },
  featureIcon: { fontSize: '20px', minWidth: '24px', textAlign: 'center' },
  featureText: { color: '#444', fontSize: '14px', fontWeight: '500' },
  divider: {
    borderTop: '2px solid #f0f0f0',
    margin: '0 0 24px',
    paddingTop: '24px',
  },
  dividerText: { color: '#999', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' },
  googleWrap: {
    display: 'flex', justifyContent: 'center', marginBottom: '20px',
  },
  legal: {
    color: '#aaa', fontSize: '12px', lineHeight: '1.6', margin: 0,
  },
};
